import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { EnvelopeSimple, PaperPlaneTilt, CheckCircle, XCircle } from "@phosphor-icons/react";

export default function Settings() {
  const [s, setS] = useState(null);
  const [alertCfg, setAlertCfg] = useState(null);
  const [alertLogs, setAlertLogs] = useState([]);
  const [testRecipient, setTestRecipient] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get("/settings").then(({data}) => setS(data)); }, []);
  useEffect(() => {
    api.get("/alerts/config").then(({data}) => { setAlertCfg(data); setTestRecipient(data.to || ""); });
    api.get("/alerts").then(({data}) => setAlertLogs(data));
  }, []);

  const save = async () => {
    const payload = { ...s };
    delete payload.owner_id;
    await api.put("/settings", payload);
    toast.success("Configurações salvas");
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/alerts/test", { recipient: testRecipient || undefined });
      toast.success(`E-mail de teste enviado (${data.id?.slice(0, 8) || "ok"})`);
      const { data: logs } = await api.get("/alerts");
      setAlertLogs(logs);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar teste");
    } finally { setSending(false); }
  };

  if (!s) return <div className="text-sm text-muted-foreground">Carregando...</div>;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Configurações</div>
        <h1 className="font-heading font-extrabold text-4xl">Preferências de monitoramento</h1>
      </div>

      <Card className="p-6 rounded-sm border max-w-3xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label className="text-xs uppercase">Frequência da varredura</Label>
            <Select value={s.frequencia_varredura} onValueChange={(v) => setS({ ...s, frequencia_varredura: v })}>
              <SelectTrigger data-testid="freq-select" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="semanal">Semanal</SelectItem>
                <SelectItem value="quinzenal">Quinzenal</SelectItem>
                <SelectItem value="mensal">Mensal</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase">E-mail para alertas</Label>
            <Input data-testid="email-input" type="email" value={s.email_notificacao} onChange={(e) => setS({ ...s, email_notificacao: e.target.value })} className="rounded-sm mt-1" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase">Limite de similaridade para alerta (%): {s.limite_similaridade}</Label>
            <Slider min={20} max={100} step={5} value={[s.limite_similaridade]} onValueChange={([v]) => setS({ ...s, limite_similaridade: v })} className="mt-3" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase">Classes prioritárias INPI (vírgula)</Label>
            <Input value={s.classes_prioritarias.join(", ")} onChange={(e) => setS({ ...s, classes_prioritarias: e.target.value.split(",").map(x => x.trim()).filter(Boolean) })} className="rounded-sm mt-1" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase">Fontes ativas (vírgula)</Label>
            <Input value={s.fontes_ativas.join(", ")} onChange={(e) => setS({ ...s, fontes_ativas: e.target.value.split(",").map(x => x.trim()).filter(Boolean) })} className="rounded-sm mt-1" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase">Termos de exclusão (vírgula)</Label>
            <Input value={s.termos_exclusao.join(", ")} onChange={(e) => setS({ ...s, termos_exclusao: e.target.value.split(",").map(x => x.trim()).filter(Boolean) })} className="rounded-sm mt-1" />
          </div>
        </div>
        <Button data-testid="save-settings-btn" onClick={save} className="mt-6 rounded-sm bg-[#0A3B2C] hover:bg-[#083527]">Salvar preferências</Button>
      </Card>

      <Card className="p-6 rounded-sm border max-w-3xl">
        <div className="flex items-center gap-2 mb-4">
          <EnvelopeSimple size={20} weight="bold" className="text-[#0A3B2C]" />
          <h3 className="font-heading font-bold text-lg">Alertas por e-mail</h3>
        </div>
        {alertCfg && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <div className="text-[10px] uppercase text-muted-foreground">Serviço</div>
              <div className="text-sm flex items-center gap-2 mt-1">
                Resend
                <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-sm ${alertCfg.resend_configured ? "risk-baixo" : "risk-alto"}`}>
                  {alertCfg.resend_configured ? "Conectado" : "Não configurado"}
                </span>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-muted-foreground">Remetente</div>
              <div className="text-sm mt-1 font-mono break-all">{alertCfg.from}</div>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase">Destinatário para alerta de RISCO ALTO</Label>
              <div className="flex gap-2 mt-1">
                <Input data-testid="alert-recipient" value={testRecipient} onChange={(e) => setTestRecipient(e.target.value)} className="rounded-sm" />
                <Button data-testid="alert-test-btn" onClick={sendTest} disabled={sending} className="rounded-sm bg-[#0A3B2C] hover:bg-[#083527] shrink-0">
                  <PaperPlaneTilt size={14} className="mr-2" /> {sending ? "Enviando..." : "Enviar teste"}
                </Button>
              </div>
              <div className="text-[10px] text-muted-foreground mt-2">
                O alerta é disparado automaticamente ao final de cada varredura que encontrar resultados de RISCO ALTO.
              </div>
            </div>
          </div>
        )}

        <div className="border-t pt-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Histórico de alertas</div>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {alertLogs.length === 0 && <div className="text-xs text-muted-foreground py-2">Nenhum alerta enviado ainda.</div>}
            {alertLogs.map(a => (
              <div key={a.id} className="flex items-center justify-between text-xs py-2 border-b">
                <div className="flex items-center gap-2">
                  {a.status === "enviado"
                    ? <CheckCircle size={14} weight="bold" className="text-[#047857]" />
                    : <XCircle size={14} weight="bold" className="text-[#DC2626]" />}
                  <div>
                    <div className="font-semibold">{a.brand_name} — {a.high_risk_count} risco(s) alto(s)</div>
                    <div className="text-[10px] text-muted-foreground font-mono">{a.recipient} • {new Date(a.sent_at).toLocaleString("pt-BR")}</div>
                  </div>
                </div>
                <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-sm ${a.status === "enviado" ? "risk-baixo" : "risk-alto"}`}>{a.status}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <Card className="p-6 rounded-sm border max-w-3xl">
        <h3 className="font-heading font-bold text-lg mb-3">Integrações externas</h3>
        <div className="space-y-2 text-sm">
          {[
            { name: "SerpApi / Google Programmable Search", status: alertCfg?.serpapi_configured ? "conectado" : "pendente" },
            { name: "Google Maps API", status: "pendente" },
            { name: "Google Cloud Vision (comparação de imagens)", status: "pendente" },
            { name: "OpenAI GPT-5.2 (análise IA)", status: "conectado" },
            { name: "Resend (alertas por e-mail)", status: alertCfg?.resend_configured ? "conectado" : "pendente" },
            { name: "INPI (API/CSV/manual)", status: "pendente" },
            { name: "Instagram / Facebook / LinkedIn / YouTube", status: "pendente" },
            { name: "n8n Webhook", status: "pendente" },
          ].map(i => (
            <div key={i.name} className="flex justify-between items-center py-2 border-b">
              <span>{i.name}</span>
              <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-sm ${i.status === "conectado" ? "risk-baixo" : "risk-medio"}`}>
                {i.status === "conectado" ? "Conectado" : "Integração pendente"}
              </span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
